import {
  Button,
  ButtonsBlock,
  DeleteButton,
  DeleteButtonBlock,
  Loader,
  PopupEventWrapper,
  Sidebar,
  SidebarFooter,
  SidebarHeader,
  Tabs,
} from 'components';
import { ContentSlot, DialogTextArea } from 'components/PopupEvent/styled';
import { EditButtonBlock } from 'components/Shared/styled';
import { EditEventType } from 'constants/sidebars';
import { useClickOutsideClose } from 'hooks';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMedia } from 'react-use';
import { initialFiltersState } from 'sidebars/CreateEventOverall/utils';
import { closeModal, openModal } from 'store/actionCreators/modal';
import {
  getCurrentCommunity,
  getCurrentUserInCommunity,
} from 'store/selectors/currentCommunity';
import { getCurrentGroup } from 'store/selectors/currentGroup';
import { getDeleteEventLoading } from 'store/selectors/events';
import { getData } from 'store/selectors/modal';
import { breakpoints } from 'theme';
import { CommunityRoles, EventDetailsSidebarData, ModalTypes } from 'types';
import {
  getProfileName,
  getUTCDateFromDate,
  isNotifyPopupDisabled,
} from 'utils';

import { useSingleEvent } from '../../hooks/useSingleEvent';
import { NotificationType } from '../../pages/CommunitySettings/NotificationsTab/types';
import { isAdminOrOwner } from '../../utils/roles';
import DocumentsTab from './Documents';
import EventTab from './Event';
import { useEventDetails } from './hooks';
import ParticipantsTab from './Participants';
import TasksTab from './Tasks';

const EventDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDownMd = useMedia(breakpoints.downMd);
  const navigate = useNavigate();
  const location = useLocation();
  const [notifyPopup, setNotifyPopup] = useState<{
    isOpen: boolean;
    type?: EditEventType;
    notifyText?: string;
  }>({
    isOpen: false,
  });
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const currentGroup = useSelector(getCurrentGroup);
  const modalData = useSelector(getData) as EventDetailsSidebarData;
  const {
    filterFrom,
    filterTo,
    openTotal,
    eventResponseId,
    width,
    date,
    eventId,
  } = modalData;
  const community = useSelector(getCurrentCommunity);
  const group = useSelector(getCurrentGroup);
  const currentUser = useSelector(getCurrentUserInCommunity);
  const isDeleteLoading = useSelector(getDeleteEventLoading);

  const event = useSingleEvent(community.id, eventId);

  const isOverallEvent = !!event?.communityId && !event?.groupId;
  const tasks = community?.eventTasks.filter((task) =>
    event?.tasks.includes(task.id),
  );
  const refModal = React.createRef<HTMLDivElement>();

  const closeSidebar = () => {
    if (location.pathname.includes('/event/')) {
      navigate(location.pathname.split('/event/')[0], { replace: true });
    }

    dispatch(closeModal());
  };

  const handleTabChange = () => {
    setSelectedTask(null);
  };

  useClickOutsideClose({ ref: refModal, callback: closeSidebar });

  const eventUsers =
    (group?.id ? group.users?.data : community?.users)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ?.filter((user) => event?.recipients.includes(user.id))
      .map((user) => {
        const data = currentGroup?.users?.data;
        return {
          ...user.profile,
          groupPermission: data
            ? data.find((u) => u.id === user.id)?.groupPermission
            : null,
          communityPermission: user.communityPermission,
          user,
        };
      }) || [];

  const { onDeleteEvents } = useEventDetails({
    groupId: group?.id,
    communityId: community?.id,
    eventId: event?.id,
    event,
    isOverallEvent,
  });

  const openCreateEventSidebar = () => {
    if (location.pathname.includes('/event/')) {
      navigate(location.pathname.split('/event/')[0], { replace: true });
    }

    dispatch(
      openModal({
        type: isOverallEvent
          ? ModalTypes.CREATE_EVENT_OVERALL
          : ModalTypes.CREATE_EVENT,
        placement: 'right',
        data: isOverallEvent
          ? {
              overallScheduler: community.overallScheduler,
              event,
              date,
              filterFrom,
              filterTo: initialFiltersState().to,
            }
          : {
              groupId: event?.groupId,
              event,
              date,
              filterFrom,
              filterTo: initialFiltersState().to,
            },
      }),
    );
  };

  const DeleteButtonComponent = ({ onClick = () => {} }) => (
    <DeleteButton withLabel type="button" onClick={onClick}>
      {isDeleteLoading ? (
        <Loader type="button" size="28px" thickness={2} />
      ) : null}
    </DeleteButton>
  );

  const EditButtonComponent = ({ onClick }) => (
    <Button
      type="button"
      width="100%"
      disabled={isDeleteLoading}
      onClick={onClick}
    >
      {t('common.edit')}
    </Button>
  );

  const handleViewReplyClick = () => {
    const subReply = event?.subEventReplies
      ?.filter((r) => r.userId === currentUser.id)
      .filter(
        (r) =>
          new Date(r.date).getTime() ===
          getUTCDateFromDate(new Date(date)).getTime(),
      )[0];
    const subTaskReplies = event?.subEventTaskReplies
      ?.filter((r) => r.userId === currentUser.id)
      .filter(
        (r) =>
          new Date(r.date).getTime() ===
          getUTCDateFromDate(new Date(date)).getTime(),
      )
      .map((task) => task.eventTaskId);

    closeSidebar();

    dispatch(
      openModal({
        type: ModalTypes.GIVE_REPLY,
        placement: 'right',
        data: {
          userId: currentUser.id,
          isViewOnly: false,
          isAdmin: isAdminOrOwner(currentUser),
          subReply: subReply
            ? {
                ...subReply,
                tasks: subTaskReplies,
              }
            : null,
          eventId: event?.id,
          date: new Date(event?.from),
          name: getProfileName(currentUser.profile),
          filterFrom,
          filterTo,
        },
      }),
    );
  };

  const onDeleteButtonClick = (type: EditEventType) => {
    const ignoreNotifyPopup = isNotifyPopupDisabled(
      NotificationType.EVENT_DELETE,
      event,
      community.notifications,
    );

    if (ignoreNotifyPopup) {
      return onDeleteEvents(type, false);
    }

    setNotifyPopup({ isOpen: true, type });
  };

  const deleteSelectionOptions = [
    {
      value: EditEventType.SINGLE_EVENT,
      label: t('event.eventOperations.eventOnly'),
      handleConfirm: () => onDeleteButtonClick(EditEventType.SINGLE_EVENT),
    },
    {
      value: EditEventType.SINGLE_AND_FOLLOW,
      label: t('event.eventOperations.eventAndFollowUp'),
      handleConfirm: () => onDeleteButtonClick(EditEventType.SINGLE_AND_FOLLOW),
    },
    {
      value: EditEventType.FULL_EVENT,
      label: t('event.eventOperations.all'),
      handleConfirm: () => onDeleteButtonClick(EditEventType.FULL_EVENT),
    },
  ];

  const deletePopupButtonsOne = [
    {
      popupButtonText: t('common.ok'),
      handleConfirm: () => onDeleteButtonClick(EditEventType.SINGLE_EVENT),
    },
    {
      popupButtonText: t('common.cancel'),
      handleConfirm: () => {},
    },
  ];

  const popupButtons = [
    {
      popupButtonText: t('communitySettings.notificationPopupBack'),
      variant: 'text',
      handleConfirm: () => setNotifyPopup({ isOpen: false }),
    },
    {
      popupButtonText: t('communitySettings.notificationPopupDoNotNotify'),
      variant: 'text',
      handleConfirm: () => {
        onDeleteEvents(notifyPopup.type, false);
        setNotifyPopup({ isOpen: false });
      },
    },
    {
      popupButtonText: t('communitySettings.notificationPopupNotify'),
      handleConfirm: () => {
        onDeleteEvents(notifyPopup.type, true, notifyPopup.notifyText);
        setNotifyPopup({ isOpen: false });
      },
    },
  ];

  if (!event) {
    return null;
  }

  return (
    <Sidebar width={width} ref={refModal}>
      <SidebarHeader
        label={
          selectedTask
            ? tasks.filter((task) => task.id === selectedTask)[0].title
            : t('event.eventDetails')
        }
        onCloseClick={closeSidebar}
        onReturnClick={selectedTask ? () => setSelectedTask(null) : null}
      />
      {notifyPopup.isOpen && (
        <PopupEventWrapper
          innerComponent={
            <ContentSlot>
              <DialogTextArea
                value={notifyPopup.notifyText}
                placeholder={t(
                  'communitySettings.notificationPopupMessageLabel',
                )}
                rows={4}
                onChange={(e) =>
                  setNotifyPopup({
                    ...notifyPopup,
                    notifyText: e.target.value,
                  })
                }
              />
            </ContentSlot>
          }
          isOpen={true}
          popupMessageText={t('communitySettings.notificationPopupHeader')}
          popupButtons={popupButtons}
          enhancedLayout
          stackFooterActions={isDownMd}
          popupStyles={{
            width: isDownMd ? '94%' : '520px',
            maxWidth: '520px',
          }}
          darkBackground={true}
          onPopupClose={() => setNotifyPopup({ isOpen: false })}
        />
      )}
      <Tabs isSidebar onTabChange={handleTabChange}>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <div label={t('event.event')} index={0}>
          <EventTab
            event={event}
            currentUser={currentUser}
            date={date}
            width={width}
            eventResponseId={eventResponseId}
            openTotal={openTotal}
            onViewReplyClick={handleViewReplyClick}
          />
        </div>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <div label={t('common.participants')} index={1}>
          <ParticipantsTab users={eventUsers} maxWidth="180px" />
        </div>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <div label={t('common.tasks')} index={2}>
          <TasksTab
            tasks={tasks}
            selectedTask={selectedTask}
            setSelectedTask={setSelectedTask}
            event={event}
            users={eventUsers}
            date={date}
          />
        </div>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <div label={t('common.documentation')} iconLabel grow index={3}>
          <DocumentsTab documents={event.documents} />
        </div>
      </Tabs>
      <SidebarFooter>
        {!(
          isOverallEvent &&
          ![CommunityRoles.OWNER, CommunityRoles.ADMIN].includes(
            currentUser.communityPermission.role,
          )
        ) && (
          <ButtonsBlock>
            <DeleteButtonBlock>
              {event.eventSeriesId ? (
                <PopupEventWrapper
                  popupMessageText={t('event.eventOperations.deleteText')}
                  selectionOptions={deleteSelectionOptions}
                  defaultSelectionValue={EditEventType.SINGLE_EVENT}
                  darkBackground={true}
                >
                  <DeleteButtonComponent />
                </PopupEventWrapper>
              ) : (
                <PopupEventWrapper
                  popupMessageText={t('groupSettings.deleteGroupHeader')}
                  popupButtons={deletePopupButtonsOne}
                  darkBackground={true}
                >
                  <DeleteButtonComponent />
                </PopupEventWrapper>
              )}
            </DeleteButtonBlock>
            <EditButtonBlock>
              <EditButtonComponent onClick={openCreateEventSidebar} />
            </EditButtonBlock>
          </ButtonsBlock>
        )}
      </SidebarFooter>
    </Sidebar>
  );
};

export default EventDetails;
